/**
 * A rubric as learners read it, in the brief and in the teacher's Learner
 * preview: each criterion with its points and, for a level rubric, what to look
 * for and every shared level with its exact points and, when written, what earns
 * it on that criterion. Callers pass the terms the attempt answers.
 */
import { a3Copy } from "@/components/assignments/a3-copy";
import type { Feedback, RubricCriterion } from "@/lib/api/assignments";
import { levelPoints, pointsWord } from "./rubric-levels";

const said = (value: number) => Number.isFinite(value) ? pointsWord(value) : "?";
const pointsOf = (value: number) => `${said(value)} ${value === 1 ? "point" : "points"}`;

export function RubricView({ rubric }: { rubric: RubricCriterion[] }) {
  const levels = rubric.find(criterion => criterion.levels?.length)?.levels ?? [];
  const total = rubric.reduce((sum, criterion) => sum + Math.round(Number(criterion.points) * 100), 0) / 100;
  return <div data-rubric-terms className="grid gap-2">
    <ul className="grid gap-3 text-ui">
      {rubric.map(criterion => {
        const points = Number(criterion.points);
        return <li key={criterion.id} data-brief-criterion={criterion.id} className="grid gap-1">
          <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1"><bdi className="min-w-0 font-medium text-foreground [overflow-wrap:anywhere]">{criterion.label}</bdi>{" · "}<span dir="ltr" className="whitespace-nowrap">{pointsOf(points)}</span></p>
          {criterion.description?.trim() && <p dir="auto" className="whitespace-pre-wrap text-muted-foreground">{criterion.description.trim()}</p>}
          {criterion.levels?.length ? <ul className="grid gap-0.5 text-meta text-muted-foreground">
            {criterion.levels.map(level => <li key={level.id} data-brief-level={level.id}>
              <bdi className="font-medium text-foreground">{level.name}</bdi> · <bdi dir="ltr">{Number.isFinite(points) && Number.isFinite(Number(level.share)) ? pointsWord(levelPoints(points, Number(level.share))) : "?"}</bdi>{level.evidence?.trim() && <>: <bdi>{level.evidence.trim()}</bdi></>}
            </li>)}
          </ul> : null}
        </li>;
      })}
    </ul>
    {levels.length > 0 && <p className="text-meta text-muted-foreground">
      Levels: {levels.map((level, index) => <span key={level.id}>{index > 0 && ", "}<bdi>{level.name}</bdi> <bdi dir="ltr">{level.share}%</bdi></span>)} of a criterion's points, to the hundredth of a point. {a3Copy.totalPoints(said(total))}
    </p>}
  </div>;
}

/** Literal labels and numeric awards have separate bidi boundaries in every
 * judgement surface. Original rubric/feedback records remain unchanged. */
export function RubricResults({ rubric, event, showUnrecorded = false }: { showUnrecorded?: boolean; rubric: RubricCriterion[] | null | undefined; event: Pick<Feedback, "criterion_scores" | "criterion_levels"> }) {
  if (!rubric?.length || (!event.criterion_scores && !event.criterion_levels)) return null;
  return <ul data-criterion-results className="grid gap-1 text-meta">
    {rubric.map(criterion => {
      const mark = event.criterion_levels?.[criterion.id];
      const score = mark?.awarded ?? event.criterion_scores?.[criterion.id];
      if (score == null && !showUnrecorded) return null;
      const level = mark ? criterion.levels?.find(item => item.id === mark.level_id)?.name ?? mark.level_id : null;
      return <li key={criterion.id} data-criterion-result={criterion.id} className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        <bdi className="min-w-0 [overflow-wrap:anywhere]">{criterion.label}</bdi>
        {level ? <>: <bdi>{level}</bdi>, </> : " "}
        <span dir="ltr" className="whitespace-nowrap">{score == null ? "Not recorded" : `${said(Number(score))} of ${said(Number(criterion.points))}`}</span>
        {mark?.reason && <span> (<bdi dir="ltr">{said(mark.baseline)}</bdi> for <bdi>{level}</bdi>: <bdi>{mark.reason}</bdi>)</span>}
      </li>;
    })}
  </ul>;
}
